
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, FileText, Pencil, Archive, Save, X } from "lucide-react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Dummy product data
const dummyProducts = [
  {
    id: 1,
    name: "Organic Green Apple",
    sku: "FRUIT-001",
    barcode: "8901234567890",
    category: "Fruits",
    brand: "Nature's Best",
    packSize: "1 kg",
    purchasePrice: 2.50,
    sellingPricePOS: 3.99,
    sellingPriceJimatShop: 3.89,
    margin: 37.3,
    tax: true,
    image: "https://images.unsplash.com/photo-1501286353178-1ec871214838",
    suppliers: ["Fresh Farm", "Organic Supplies"],
    channels: ["POS", "JimatShop"],
    stock: 25,
    lowStockThreshold: 10,
  },
  {
    id: 2,
    name: "Premium Mineral Water",
    sku: "BEVR-102",
    barcode: "8901234567891",
    category: "Beverages",
    brand: "AquaPure",
    packSize: "500 ml",
    purchasePrice: 0.30,
    sellingPricePOS: 0.99,
    sellingPriceJimatShop: 0.89,
    margin: 69.7,
    tax: false,
    image: "https://images.unsplash.com/photo-1582562124811-c09040d0a901",
    suppliers: ["Beverage Wholesale"],
    channels: ["POS"],
    stock: 5,
    lowStockThreshold: 10,
  },
  {
    id: 3,
    name: "Chocolate Cookies",
    sku: "SNCK-203",
    barcode: "8901234567892",
    category: "Snacks",
    brand: "Sweet Treats",
    packSize: "200g",
    purchasePrice: 1.20,
    sellingPricePOS: 2.49,
    sellingPriceJimatShop: 2.29,
    margin: 51.8,
    tax: true,
    image: "https://images.unsplash.com/photo-1618160702438-9b02ab6515c9",
    suppliers: ["Snack Distributors", "Sweet Wholesale"],
    channels: ["POS", "JimatShop"],
    stock: 0,
    lowStockThreshold: 5,
  },
  {
    id: 4,
    name: "Fresh Bread Loaf",
    sku: "BAKE-304",
    barcode: "8901234567893",
    category: "Bakery",
    brand: "Daily Fresh",
    packSize: "400g",
    purchasePrice: 0.80,
    sellingPricePOS: 1.99,
    sellingPriceJimatShop: 1.89,
    margin: 59.8,
    tax: false,
    image: "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07",
    suppliers: ["Local Bakery", "Food Supplies"],
    channels: ["JimatShop"],
    stock: 15,
    lowStockThreshold: 10,
  },
];

// Type definition for product
type Product = typeof dummyProducts[0];

const AllProductsTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [products, setProducts] = useState(dummyProducts);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [tempPrices, setTempPrices] = useState({
    sellingPricePOS: "",
    sellingPriceJimatShop: "",
  });
  const [inlineEditingId, setInlineEditingId] = useState<number | null>(null);
  const [inlineEditValues, setInlineEditValues] = useState({
    sellingPricePOS: "",
    sellingPriceJimatShop: "",
  });

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "" || categoryFilter === "all" || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const uniqueCategories = Array.from(new Set(products.map(product => product.category)));

  // Get stock status with color
  const getStockStatus = (product: Product) => {
    if (product.stock <= 0) {
      return { text: "Out of Stock", color: "text-red-600 bg-red-50" };
    } else if (product.stock < product.lowStockThreshold) {
      return { text: `Low Stock (${product.stock})`, color: "text-amber-600 bg-amber-50" };
    } else {
      return { text: `${product.stock}`, color: "text-green-600" };
    }
  };

  // Start inline editing
  const handleStartInlineEdit = (product: Product) => {
    setInlineEditingId(product.id);
    setInlineEditValues({
      sellingPricePOS: product.sellingPricePOS.toFixed(2),
      sellingPriceJimatShop: product.sellingPriceJimatShop.toFixed(2),
    });
  };

  // Save inline edit
  const handleSaveInlineEdit = () => {
    if (inlineEditingId === null) return;
    
    const updatedProducts = products.map(product => {
      if (product.id === inlineEditingId) {
        const newPOS = parseFloat(inlineEditValues.sellingPricePOS) || product.sellingPricePOS;
        const newJimatShop = parseFloat(inlineEditValues.sellingPriceJimatShop) || product.sellingPriceJimatShop;
        
        return {
          ...product,
          sellingPricePOS: newPOS,
          sellingPriceJimatShop: newJimatShop,
        };
      }
      return product;
    });
    
    setProducts(updatedProducts);
    setInlineEditingId(null);
  };

  // Cancel inline edit
  const handleCancelInlineEdit = () => {
    setInlineEditingId(null);
  };

  // Handle inline edit input change with decimal validation
  const handleInlineInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'sellingPricePOS' | 'sellingPriceJimatShop') => {
    const value = e.target.value;
    
    // Allow empty string, digits, and only one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setInlineEditValues({
        ...inlineEditValues,
        [field]: value,
      });
    }
  };

  // Open price edit dialog
  const handleEditPrices = (product: Product) => {
    setEditingProduct(product);
    setTempPrices({
      sellingPricePOS: product.sellingPricePOS.toString(),
      sellingPriceJimatShop: product.sellingPriceJimatShop.toString(),
    });
    setIsEditDialogOpen(true);
  };

  // Save edited prices
  const handleSavePrices = () => {
    if (!editingProduct) return;
    
    const updatedProducts = products.map(product => {
      if (product.id === editingProduct.id) {
        return {
          ...product,
          sellingPricePOS: parseFloat(tempPrices.sellingPricePOS) || product.sellingPricePOS,
          sellingPriceJimatShop: parseFloat(tempPrices.sellingPriceJimatShop) || product.sellingPriceJimatShop,
        };
      }
      return product;
    });
    
    setProducts(updatedProducts);
    setIsEditDialogOpen(false);
    setEditingProduct(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {uniqueCategories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Selling Price (POS)</TableHead>
              <TableHead>Selling Price (JimatShop)</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Channels</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product) => {
                const stockStatus = getStockStatus(product);
                const isEditing = inlineEditingId === product.id;
                
                return (
                  <TableRow key={product.id}>
                    <TableCell>
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="h-10 w-10 rounded-md object-cover"
                      />
                    </TableCell>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell>{product.sku}</TableCell>
                    <TableCell>{product.category}</TableCell>
                    
                    {/* POS Price column with inline editing */}
                    <TableCell>
                      {isEditing ? (
                        <Input
                          type="text"
                          value={inlineEditValues.sellingPricePOS}
                          onChange={(e) => handleInlineInputChange(e, 'sellingPricePOS')}
                          className="h-8 w-24"
                        />
                      ) : (
                        <span 
                          className="cursor-pointer hover:text-blue-600" 
                          onClick={() => handleStartInlineEdit(product)}
                        >
                          ${product.sellingPricePOS.toFixed(2)}
                        </span>
                      )}
                    </TableCell>
                    
                    {/* JimatShop Price column with inline editing */}
                    <TableCell>
                      {isEditing ? (
                        <Input
                          type="text"
                          value={inlineEditValues.sellingPriceJimatShop}
                          onChange={(e) => handleInlineInputChange(e, 'sellingPriceJimatShop')}
                          className="h-8 w-24"
                        />
                      ) : (
                        <span 
                          className="cursor-pointer hover:text-blue-600" 
                          onClick={() => handleStartInlineEdit(product)}
                        >
                          ${product.sellingPriceJimatShop.toFixed(2)}
                        </span>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {product.stock <= 0 ? (
                        <span className="px-2 py-1 rounded-md text-xs font-medium text-red-600 bg-red-50">
                          Out of Stock
                        </span>
                      ) : product.stock < product.lowStockThreshold ? (
                        <span className="px-2 py-1 rounded-md text-xs font-medium text-amber-600 bg-amber-50">
                          {product.stock} (Low Stock)
                        </span>
                      ) : (
                        <span className="text-green-600">{product.stock}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {product.channels.includes("POS") && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">POS</span>
                        )}
                        {product.channels.includes("JimatShop") && (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">JimatShop</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="icon" variant="ghost" onClick={handleSaveInlineEdit}>
                                    <Save className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Save Changes</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="icon" variant="ghost" onClick={handleCancelInlineEdit}>
                                    <X className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Cancel</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </>
                        ) : (
                          <>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="icon" variant="ghost" onClick={() => handleStartInlineEdit(product)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Edit Prices</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="icon" variant="ghost">
                                    <Archive className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Archive Product</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-4 text-muted-foreground">
                  No products found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Price Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Product Prices</DialogTitle>
            <DialogDescription>
              Update the selling prices for {editingProduct?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Selling Price (POS)</label>
              <Input 
                type="text" 
                inputMode="decimal"
                value={tempPrices.sellingPricePOS}
                onChange={(e) => setTempPrices({...tempPrices, sellingPricePOS: e.target.value})}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Selling Price (JimatShop)</label>
              <Input 
                type="text" 
                inputMode="decimal"
                value={tempPrices.sellingPriceJimatShop}
                onChange={(e) => setTempPrices({...tempPrices, sellingPriceJimatShop: e.target.value})}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePrices} className="bg-green-600 hover:bg-green-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllProductsTab;
